import AppActivityIndicator from "@toothfairy/shared-ui/AppActivityIndicator";
import AppAssets from "@toothfairy/shared-ui/AppAssets";
import AppExpander from "@toothfairy/shared-ui/AppExpander";
import AppForms from "@toothfairy/shared-ui/AppForms";
import AppPlaceholderView from "@toothfairy/shared-ui/AppPlaceholderView";
import AppTable from "@toothfairy/shared-ui/AppTable";
import AppTextChip from "@toothfairy/shared-ui/AppTextChip";
import AppTheme from "@toothfairy/shared-ui/AppTheme";
import { AppText } from "@toothfairy/shared-ui";
import Constants from "../../../Constants";
import ToothFairyModal from "../../../Components/ToothFairyComponents/ToothFairyModal";
import dayjs from "dayjs";
import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { Platform, StyleSheet, View } from "react-native";
import useSections from "../../../Hooks/useSections";
import { isLargeScreen } from "../../MainViewContainer";
import { isElectronRunning } from "../../../../App";
import SupportDateLeaf from "../SubComponents/SupportDateLeaf";
import SupportLogView from "./SubComponents/SupportLogView";

const MAX_LOGABLE_HOURS = 8;

const SupportView = ({
  workspace,
  logs = [],
  usedHours: usedHoursProp,
  isLogsLoading = false,
  isSubmitting = false,
  isDeleting = false,
  canLogSupport = false,
  onSubmit = () => {},
  onDeleteLog = () => {},
}) => {
  const { t } = useTranslation();
  const { currentTheme } = AppTheme.useTheme();
  const { marginLeftUnderAppExpander } = useSections();
  const styles = generateStyles(currentTheme);

  const [showLogModal, setShowLogModal] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(null);

  const totalHours = workspace?.supportHours || 0;
  // Support hours are a budget: used hours must reflect every support log
  // associated with the workspace (summed by the container), not only the
  // hours logged within the current billing cycle.
  const usedHours = usedHoursProp ?? (workspace?.supportHoursUsed || 0);
  const remainingHours = Math.max(0, totalHours - usedHours);
  const hasSupportBudget = totalHours > 0;
  const noBudgetLeft = remainingHours <= 0;

  const openSupportPortal = () => {
    const url = "https://toothfairyai.com/en/contact";
    if (isElectronRunning() && window?.electronAPI?.openUrlOnWeb) {
      window.electronAPI.openUrlOnWeb(url);
    } else {
      window.open(url, "_blank");
    }
  };

  const renderBudgetChip = (
    icon,
    valueLabel,
    caption,
    backgroundColor,
    fontColor,
  ) => (
    <View style={styles.budgetStat}>
      <AppTextChip
        label={valueLabel}
        leftIcon={icon}
        leftIconColor={fontColor}
        iconSize={12}
        borderRadius={12}
        backgroundColor={backgroundColor}
        borderColor={backgroundColor}
        fontColor={fontColor}
        fontWeight="bold"
        preventTranslation
        styleContainer={styles.budgetChip}
        styleLabel={styles.budgetChipLabel}
      />
      <AppText
        preventTranslation
        fontSize={11}
        color={currentTheme?.theme?.secondary_font_color}
        wrapperStyle={{ marginTop: 4 }}
      >
        {caption}
      </AppText>
    </View>
  );

  const supportSchema = AppForms.yup.object().shape({
    supportHours: AppForms.yup
      .number()
      .min(0.5, t("admin.support.validation.hoursMin"))
      .max(
        Math.max(0.5, remainingHours),
        t("admin.support.validation.hoursMax"),
      )
      .required(t("admin.support.validation.hoursRequired")),
    description: AppForms.yup
      .string()
      .required(t("admin.support.validation.descriptionRequired")),
  });

  const columns = [
    {
      title: t("admin.support.history.datetime"),
      key: "datetime",
      width: 0.25,
    },
    {
      title: t("admin.support.history.engineer"),
      key: "engineer",
      width: 0.25,
    },
    { title: t("admin.support.history.hours"), key: "hours", width: 0.15 },
    {
      title: t("admin.support.history.description"),
      key: "description",
      width: 0.35,
    },
    {
      title: t("admin.support.history.actions"),
      isAction: true,
      width: 0.00001,
    },
  ];

  return (
    <View
      style={{
        width: Platform.OS === "web" && isLargeScreen() ? 550 : 350,
        marginTop: 20,
        marginLeft: marginLeftUnderAppExpander,
        alignSelf: "center",
      }}
    >
      <AppExpander
        title={t("admin.support.logTitle")}
        icon={AppAssets.icons.clock}
        style={{ minWidth: isLargeScreen() ? 530 : 250 }}
        isExpandedOnLoad={true}
      >
        {!hasSupportBudget ? (
          <View
            style={{
              paddingLeft: 0,
            }}
          >
            <AppPlaceholderView
              wrapperStyle={{
                width: "100%",
                height: 260,
                marginHorizontal: 0,
                alignItems: "center",
                justifyContent: "center",
                paddingLeft: 0,
              }}
              size={30}
              icon={AppAssets?.icons?.support}
              mainCaption={t("admin.support.noSupportTitle")}
              subCaption={t("admin.support.noSupportSubtitle")}
              actionLabel={t("admin.support.contactUs")}
              onActionPress={openSupportPortal}
            />
          </View>
        ) : (
          <>
            <View style={styles.budgetRow}>
              {renderBudgetChip(
                AppAssets.icons.clock,
                `${totalHours} h`,
                t("admin.support.budget.total"),
                currentTheme?.theme?.primary,
                currentTheme?.theme?.white,
              )}
              {renderBudgetChip(
                AppAssets.icons.bolt,
                `${usedHours} h`,
                t("admin.support.budget.used"),
                currentTheme?.theme?.lightGrey,
                currentTheme?.theme?.darkGrey,
              )}
              {renderBudgetChip(
                AppAssets.icons.check,
                `${remainingHours} h`,
                t("admin.support.budget.remaining"),
                noBudgetLeft
                  ? currentTheme?.theme?.red
                  : currentTheme?.theme?.green,
                currentTheme?.theme?.white,
              )}
            </View>

            {!canLogSupport ? (
              <AppText
                color={currentTheme?.theme?.secondary_font_color}
                fontSize={13}
                wrapperStyle={{ marginHorizontal: 10, marginVertical: 8 }}
              >
                {t("admin.support.readOnlyHint")}
              </AppText>
            ) : noBudgetLeft ? (
              <AppText
                color={currentTheme?.theme?.danger || currentTheme?.theme?.red}
                fontSize={13}
                wrapperStyle={{ marginHorizontal: 10, marginVertical: 8 }}
              >
                {t("admin.support.noBudget")}
              </AppText>
            ) : (
              <AppForms.AppForm
                style={{
                  minWidth:
                    Platform.OS === "web" && isLargeScreen() ? 350 : 250,
                  paddingLeft: 0,
                  // maxWidth: 350,
                  // alignSelf: "center",
                }}
                initialValues={{
                  supportHours: 0.5,
                  description: "",
                  activityDate: dayjs().startOf("day").toISOString(),
                }}
                enableReinitialize
                onSubmit={onSubmit}
                validationSchema={supportSchema}
              >
                <SupportDateLeaf label={t("admin.support.form.dateLabel")} />
                <AppForms.AppFormTrackSlider
                  containerStyle={{
                    marginTop: 10,
                  }}
                  step={0.5}
                  minimumValue={0.5}
                  maximumValue={Math.min(MAX_LOGABLE_HOURS, remainingHours)}
                  unitLabel={" h"}
                  label={t("admin.support.form.hoursLabel")}
                  name="supportHours"
                  isMandatory
                />
                <AppForms.AppFormField
                  showTextInputLabel
                  wrapperInputStyle={{
                    height: 300,
                    marginBottom: 10,
                    padding: 5,
                  }}
                  textContainer={{
                    padding: 10,
                    marginHorizontal: 0,
                    minHeight: 240,
                  }}
                  multiline
                  isMandatory
                  label={t("admin.support.form.descriptionLabel")}
                  placeholder={t("admin.support.form.descriptionPlaceholder")}
                  keyboardtype="default"
                  textContextType="label"
                  autoCorrect={false}
                  name="description"
                />
                <AppForms.AppSubmitButton
                  title={t("admin.support.form.submit")}
                  isDisabled={isSubmitting}
                  awesomeIconColor={currentTheme?.theme?.white}
                  backgroundColor={currentTheme?.theme?.primary}
                  btnContentStyle={{
                    paddingLeft: 0,
                    paddingRight: 0,
                  }}
                  style={[
                    {
                      height: Constants.appButtonHeight,
                      width: "auto",
                      shadowColor: currentTheme?.theme?.transparent,
                      marginHorizontal: 10,
                    },
                  ]}
                  wrapperStyle={[
                    {
                      width: "fit-content",
                      minWidth: 150,
                      marginTop: 20,
                      alignSelf: "center",
                      height: Constants.appButtonHeight,
                    },
                  ]}
                />
              </AppForms.AppForm>
            )}
          </>
        )}
      </AppExpander>

      {hasSupportBudget && (
        <AppExpander
          title={t("admin.support.historyTitle")}
          icon={AppAssets.icons.days}
          style={{ minWidth: isLargeScreen() ? 530 : 250, marginTop: 10 }}
          isExpandedOnLoad={true}
        >
          {isLogsLoading ? (
            <AppActivityIndicator
              backgroundColor={currentTheme?.theme?.softBackgroundColor}
              visible={true}
              containerStyle={{
                backgroundColor: currentTheme?.theme?.transparent,
                minHeight: 250,
              }}
            />
          ) : (
            <View style={{ minWidth: "100%", paddingLeft: 10 }}>
              <AppTable
                hideSearch={false}
                showPagination
                searchPlaceholder={t(
                  "common.filterByAnyField",
                  "Filter by any field...",
                )}
                actionButtons
                showDropDownMenuForActions
                mainActionLabel={t("common.view", "View")}
                onClickMainAction={(item, index) => {
                  setSelectedIndex(index);
                  setShowLogModal(true);
                }}
                onActionItemPress={(id, item, _index) => {
                  const row = item || item?.item;
                  if (id === "VIEW") {
                    const viewIndex = logs?.findIndex(
                      (l) => l?.log?.id === row?.log?.id,
                    );
                    setSelectedIndex(viewIndex >= 0 ? viewIndex : _index);
                    setShowLogModal(true);
                  } else if (id === "DELETE") {
                    onDeleteLog(row?.log || row);
                  }
                }}
                actions={[
                  {
                    label: t("common.view", "View"),
                    id: "VIEW",
                    icon: AppAssets.icons.eye,
                  },
                  ...(canLogSupport
                    ? [
                        {
                          label: t("common.delete"),
                          id: "DELETE",
                          icon: AppAssets.icons.delete,
                        },
                      ]
                    : []),
                ]}
                style={{ zIndex: -1, marginTop: 15 }}
                headers={columns}
                items={logs}
                itemsPerPage={50}
              />
            </View>
          )}
        </AppExpander>
      )}

      <ToothFairyModal
        key={selectedIndex}
        modalStyle={{
          maxHeight: isLargeScreen() ? 630 : 500,
          height: "95%",
          maxWidth: isLargeScreen() ? 700 : 350,
          marginHorizontal: "auto",
          marginTop: "auto",
          marginBottom: "auto",
          margin: "auto",
        }}
        isModalOpen={showLogModal}
        setIsModalOpen={setShowLogModal}
        modalTitle={t("admin.support.detailsTitle")}
        closeModal={() => {
          setShowLogModal(false);
          setSelectedIndex(null);
        }}
      >
        <SupportLogView log={logs?.[selectedIndex]} />
      </ToothFairyModal>
    </View>
  );
};

const generateStyles = (currentTheme) =>
  StyleSheet.create({
    budgetRow: {
      flexDirection: "row",
      justifyContent: "center",
      alignItems: "flex-start",
      paddingVertical: 12,
      flexWrap: "wrap",
    },
    budgetStat: {
      alignItems: "center",
      marginHorizontal: 6,
    },
    budgetChip: {
      width: "fit-content",
      margin: 0,
      paddingHorizontal: 14,
      paddingVertical: 6,
    },
    budgetChipLabel: {
      fontSize: 13,
      marginLeft: 4,
    },
  });

export default SupportView;
